import React, { useEffect, useRef, useState } from "react";
import DatePicker from "../../components/Shared/DatePicker";
import * as XLSX from "xlsx-js-style";
import PurchaseVatReportTable from "./PurchaseVatReportTable";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { getOneMonthAgo } from "../CashInflow/CashInflow";
import SharedButton from "../../components/Shared/SharedButton";
import DropdownComponent from "../../components/Shared/DropdownComponent";
import { useDispatch, useSelector } from "react-redux";
import { getPurchaseVATReport } from "../../store/slices/ReportSlice";
import validSupergitHIS from "../../api/validSupergitHIS";
import toast from "react-hot-toast";

export default function PurchaseVatReport() {
  const [endDate, setEndDate] = useState(
    new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0],
  );
  const [startDate, setStartDate] = useState(getOneMonthAgo(endDate));
  const [isExporting, setIsExporting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [vatFilter, setVatFilter] = useState("with_vat"); // with_vat, without_vat, or all
  const dispatch = useDispatch();
  const { purchaseVatReport, isLoading } = useSelector((state) => state.reports);
  const { user } = useSelector((state) => state.auth);
  const { branches } = useSelector((state) => state.business);
  const lang = i18next.language;
  const { t } = useTranslation();

  const balanceSheetRef = useRef(null);
  const iframeRef = useRef(null);

  const vatFilterOptions = [
    { label: t("With VAT"), value: "with_vat" },
    { label: t("Without VAT"), value: "without_vat" },
    { label: t("All"), value: "all" },
  ];

  const handleSubmit = () => {
    if (startDate && endDate) {
      const payload = {
        start_date: startDate,
        end_date: endDate,
      };
      
      if (vatFilter !== "all") {
        payload.vat_filter = vatFilter;
      }
      
      dispatch(getPurchaseVATReport(payload));
    }
  };

  const handleReset = () => {
    const today = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];
    const defaultStartDate = getOneMonthAgo(today);
    setEndDate(today);
    setStartDate(defaultStartDate);
    setVatFilter("with_vat");
    
    dispatch(
      getPurchaseVATReport({
        start_date: defaultStartDate,
        end_date: today,
        vat_filter: "with_vat",
      })
    );
  };

  useEffect(() => {
    const payload = {
      start_date: startDate,
      end_date: endDate,
    };
    
    if (vatFilter !== "all") {
      payload.vat_filter = vatFilter;
    }
    
    dispatch(getPurchaseVATReport(payload));
  }, [dispatch, startDate, endDate, vatFilter]);

  const handlePrint = () => {
    const direction = lang === "ar" ? "rtl" : "ltr";
    const printContents = balanceSheetRef.current.innerHTML;
    const iframe = iframeRef.current;
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<html><head><title>${t("Purchase VAT Report")}</title>`,
    );
    doc.write(
      '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css">',
    );

    doc.write("<style>");
    doc.write(`
      @media print {
        @page {
          margin: 20px 0 20px 0;
        }
        body {
          margin: 1cm;
          direction: ${direction};
        }
        header, footer {
          display: none;
        }
        table {
          direction: ${direction};
        }
      }
    `);
    doc.write("</style>");

    doc.write("</head><body>");
    doc.write(printContents);
    doc.write("</body></html>");
    doc.close();

    iframe.onload = () => {
      iframe.contentWindow.print();
    };
  };

  const handleExcelExport = () => {
    if (!purchaseVatReport?.data || purchaseVatReport.data.length === 0) {
      toast.error(t("No data available for export"));
      return;
    }

    setIsExporting(true);

    try {      
      const branchFromRedux = user?.branch_id && branches?.data
        ? Object.values(branches.data).find((b) => b?.id === user?.branch_id)
        : null;

      const branchNameAr =
        user?.branch?.name_ar?.trim() ||
        branchFromRedux?.name_ar?.trim() ||
        user?.branch?.name_en?.trim() ||
        branchFromRedux?.name_en?.trim() ||
        "الجملية";

      const start = new Date(startDate);
      const end = new Date(endDate);
      const startMonth = start.getMonth() + 1; // 1-12
      const endMonth = end.getMonth() + 1;
      const year = end.getFullYear();

      const getQuarter = (month) => {
        if (month <= 3) return 1;
        if (month <= 6) return 2;
        if (month <= 9) return 3;
        return 4;
      };

      const quarterLabelsAr = {
        1: "الربع الأول",
        2: "الربع الثاني",
        3: "الربع الثالث",
        4: "الربع الرابع",
      };

      let periodTextAr = "";

      const endQ = getQuarter(endMonth);

      if (startMonth === 1 && endMonth === 6) {
        periodTextAr = "النصف الأول";
      } else if (startMonth === 7 && endMonth === 12) {
        periodTextAr = "النصف الثاني";
      } else {
        periodTextAr = quarterLabelsAr[endQ];
      }

      const dataRows = purchaseVatReport.data.map((item, index) => {
        const vendorName = lang === "ar" ? item.vendor?.name_ar : item.vendor?.name;
        
        if (vatFilter === "without_vat") {
          return [
            item.total_amount ? parseFloat(item.total_amount).toFixed(2) : "0.00", // Column A: المبلغ
            vendorName || "", // Column B: اسم المورد
            item.purchase_date || "", // Column C: التاريخ
            item.refrence || "", // Column D: رقم الفاتورة
          ];
        } else {
          return [
            item.vendor?.vat_no || "", // Column A: الرقم الضريبي
            item.total_vat_amount ? parseFloat(item.total_vat_amount).toFixed(2) : "0.00", // Column B: الضريبة
            item.total_amount ? parseFloat(item.total_amount).toFixed(2) : "0.00", // Column C: المبلغ
            vendorName || "", // Column D: اسم المورد
            item.purchase_date || "", // Column E: التاريخ
            item.refrence || "", // Column F: رقم الفاتورة
          ];
        }
      });

      const totalVat = purchaseVatReport.total_vat ? parseFloat(purchaseVatReport.total_vat).toFixed(2) : "0.00";
      const totalAmount = purchaseVatReport.total_amount ? parseFloat(purchaseVatReport.total_amount).toFixed(2) : "0.00";

      if (vatFilter === "without_vat") {
        dataRows.push([
          totalAmount, 
          "", 
          "", 
          "", 
        ]);
      } else {
        dataRows.push([
          "",
          totalVat, 
          totalAmount,
          "", 
          "", 
          "", 
        ]);
      }

      const headerRow = vatFilter === "without_vat"
        ? [
            "المبلغ", 
            "اسم المورد",
            "التاريخ", 
            "رقم الفاتورة", 
          ]
        : [
            "الرقم الضريبي", 
            "الضريبة", 
            "المبلغ", 
            "اسم المورد", 
            "التاريخ", 
            "رقم الفاتورة", 
          ];

      const purchaseLabel = vatFilter === "without_vat" ? "المشتريات" : "المشتريات الضريبية";
      const titleRow = [
        `${purchaseLabel} لقسم ${branchNameAr} عن ${periodTextAr} للعام ${year}`,
      ];

      const fileName = `${purchaseLabel}_${branchNameAr}_${periodTextAr}_${year}.xlsx`;

      const ws_data = [
        titleRow, 
        [], 
        headerRow, 
        ...dataRows, 
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(ws_data);

      const numCols = vatFilter === "without_vat" ? 4 : 6;
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } }, // Title merged
      ];

      if (vatFilter === "without_vat") {
        worksheet["!cols"] = [
          { wch: 12 }, 
          { wch: 40 }, 
          { wch: 15 },
          { wch: 15 }, 
        ];
      } else {
        worksheet["!cols"] = [
          { wch: 18 }, 
          { wch: 12 }, 
          { wch: 12 },
          { wch: 40 }, 
          { wch: 15 }, 
          { wch: 15 }, 
        ];
      }

      const arabicFont = "Tahoma";
      const fontSize = { title: 14, header: 11, data: 10 };

      if (worksheet["A1"]) {
        worksheet["A1"].s = {
          font: { bold: true, sz: fontSize.title, name: arabicFont },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: "FFFFFF" } },
        };
      }

      const headerCells = vatFilter === "without_vat"
        ? ["A3", "B3", "C3", "D3"]
        : ["A3", "B3", "C3", "D3", "E3", "F3"];
        
      headerCells.forEach((cell) => {
        if (worksheet[cell]) {
          worksheet[cell].s = {
            font: { bold: true, sz: fontSize.header, name: arabicFont },
            alignment: { horizontal: "center", vertical: "center" },
            fill: { fgColor: { rgb: "D3D3D3" } }, // Light gray background
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        }
      });
      const dataStartRow = 4;
      const dataEndRow = dataStartRow + dataRows.length - 1;
      const columns = vatFilter === "without_vat" ? ["A", "B", "C", "D"] : ["A", "B", "C", "D", "E", "F"];
      
      for (let row = dataStartRow; row <= dataEndRow; row++) {
        columns.forEach((col) => {
          const cellRef = `${col}${row}`;
          if (worksheet[cellRef]) {
            worksheet[cellRef].s = {
              font: { sz: fontSize.data, name: arabicFont },
              alignment: { 
                horizontal: (vatFilter === "without_vat" && col === "A") || (vatFilter !== "without_vat" && (col === "B" || col === "C")) ? "right" : "left", 
                vertical: "center" 
              },
              border: {
                top: { style: "thin", color: { rgb: "000000" } },
                bottom: { style: "thin", color: { rgb: "000000" } },
                left: { style: "thin", color: { rgb: "000000" } },
                right: { style: "thin", color: { rgb: "000000" } },
              },
            };
            
            if (row === dataEndRow) {
              worksheet[cellRef].s.font.bold = true;
              worksheet[cellRef].s.fill = { fgColor: { rgb: "F0F0F0" } };
            }
          }
        });
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase VAT Report");

      XLSX.writeFile(workbook, fileName, { bookType: "xlsx", type: "binary" });

      toast.success(t("Excel file downloaded successfully"));
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error(t("Failed to export Excel"));
    } finally {
      setIsExporting(false);
    }
  };

  const handlePDFExport = async () => {
    setIsPdfExporting(true);
    try {
      const response = await validSupergitHIS.post(
        "reports/purchase_vat_report/pdf",
        {
          start_date: startDate,
          end_date: endDate,
        },
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `purchase_vat_report_${startDate}_to_${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("PDF file downloaded successfully"));
    } catch (error) {
      toast.error(error?.response?.data?.error || t("Failed to download PDF"));
    } finally {
      setIsPdfExporting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end">
        <div className="w-full md:w-fit flex gap-2 flex-col sm:flex-row">
          <DatePicker
            useFormik={false}
            value={startDate ? new Date(startDate) : null}
            onChange={(e) => {
              setStartDate(
                e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              );
            }}
          />
          <DatePicker
            useFormik={false}
            value={endDate ? new Date(endDate) : null}
            onChange={(e) => {
              setEndDate(
                e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              );
            }}
          />
          <DropdownComponent
            useFormik={false}
            value={vatFilter}
            options={vatFilterOptions}
            handleChange={(e) => setVatFilter(e.value)}
            placeholder={t("Select VAT Filter")}
            className="w-full md:w-[200px]"
          />
          <div className="flex items-center justify-end gap-2">
            <SharedButton
              title={t("apply_filter")}
              icon="pi pi-filter"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!startDate || !endDate}
            />
            <SharedButton
              title={t("Reset")}
              icon="pi pi-sync"
              buttonType="reset"
              onClick={handleReset}
              disabled={isLoading}
            />
            <SharedButton
              title={t("Export Excel")}
              icon={"pi pi-file-excel"}
              iconSize={20}
              onClick={handleExcelExport}
              disabled={isLoading || isExporting}
              loading={isExporting}
            />
            <SharedButton
              title={t("Export PDF")}
              icon={"pi pi-print"}
              iconSize={20}
              onClick={handlePDFExport}
              disabled={isLoading || isPdfExporting}
              loading={isPdfExporting}
            />
          </div>
        </div>
      </div>

      <div className="this_div" ref={balanceSheetRef}>
        <div>
          <div>
            <div pt={{ content: { className: "!py-0" } }}>
              <div>
                <PurchaseVatReportTable
                  data={purchaseVatReport?.data}
                  totalAmount={purchaseVatReport?.total_amount}
                  totalVat={purchaseVatReport?.total_vat}
                  grandTotal={purchaseVatReport?.grand_total}
                  vatFilter={vatFilter}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <iframe ref={iframeRef} style={{ display: "none" }} title="print-frame"></iframe>
    </div>
  );
}
